type AnalyticsWindow = Window & {
  __ANCORA_IS_PROD__?: boolean;
  __ANCORA_CLICK_TRACKING_READY__?: boolean;
  __ANCORA_CLICK_TRACKING_VERSION__?: number;
  __ANCORA_BUNDLED_MARKETING_TRACKING_READY__?: boolean;
  gtag?: (...args: unknown[]) => void;
};

const MAX_TEXT_LENGTH = 80;
const BUNDLED_TRACKING_FLAG = '__ANCORA_BUNDLED_MARKETING_TRACKING_READY__';

const normalizeText = (value: string | null | undefined) =>
  String(value || '').replace(/\s+/g, ' ').trim().slice(0, MAX_TEXT_LENGTH);

const stripUrlDetails = (href: string | null, removeHash = false) => {
  if (!href) return '';
  try {
    const url = new URL(href, window.location.href);
    url.search = '';
    if (removeHash) url.hash = '';
    return url.href;
  } catch {
    const [withoutQuery] = href.split('?');
    return removeHash ? withoutQuery.split('#')[0] : withoutQuery;
  }
};

const analyticsWindow = () => window as AnalyticsWindow;

export const shouldUseMarketingTrackingFallback = (target: AnalyticsWindow) => {
  return (
    target.__ANCORA_CLICK_TRACKING_READY__ !== true &&
    target.__ANCORA_CLICK_TRACKING_VERSION__ !== 2 &&
    target.__ANCORA_IS_PROD__ === true &&
    typeof target.gtag === 'function'
  );
};

const shouldUseFallback = () => shouldUseMarketingTrackingFallback(analyticsWindow());

export const installBundledMarketingTracking = () => {
  const target = analyticsWindow();
  if (target[BUNDLED_TRACKING_FLAG]) return () => undefined;
  target[BUNDLED_TRACKING_FLAG] = true;

  const track = (eventName: string, params: Record<string, string>) => {
    if (!shouldUseFallback()) return;
    target.gtag?.('event', eventName, {
      event_category: 'conversion',
      page_path: window.location.pathname,
      page_location: stripUrlDetails(window.location.href, true),
      transport_type: 'beacon',
      ...params,
    });
  };

  const onClick = (event: MouseEvent) => {
    const element = (event.target as Element | null)?.closest<HTMLElement>('[data-marketing-event]');
    if (!element?.closest('.marketing-shell')) return;
    const eventName = normalizeText(element.dataset.marketingEvent);
    if (!eventName) return;
    const link = element.closest<HTMLAnchorElement>('a') || element.querySelector<HTMLAnchorElement>('a');
    track(eventName, {
      event_label: normalizeText(element.textContent),
      link_url: stripUrlDetails(link?.getAttribute('href') || ''),
      link_text: normalizeText(link?.textContent || element.textContent),
    });
  };

  const startedForms = new WeakSet<HTMLFormElement>();
  const onFocus = (event: FocusEvent) => {
    const form = (event.target as Element | null)?.closest<HTMLFormElement>('.marketing-shell form');
    if (!form || startedForms.has(form)) return;
    startedForms.add(form);
    track('form_start', { form_id: normalizeText(form.id || form.getAttribute('name') || 'marketing_form') });
  };
  const onSubmit = (event: SubmitEvent) => {
    const form = (event.target as Element | null)?.closest<HTMLFormElement>('.marketing-shell form');
    if (!form) return;
    track('form_submit', { form_id: normalizeText(form.id || form.getAttribute('name') || 'marketing_form') });
  };

  document.addEventListener('click', onClick);
  document.addEventListener('focusin', onFocus);
  document.addEventListener('submit', onSubmit);

  return () => {
    document.removeEventListener('click', onClick);
    document.removeEventListener('focusin', onFocus);
    document.removeEventListener('submit', onSubmit);
    target[BUNDLED_TRACKING_FLAG] = false;
  };
};
